import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Container,
  Dimmer,
  Header,
  Icon,
  Label,
  Loader,
  Menu,
  Modal,
  Table,
} from 'semantic-ui-react';
import { Link } from 'react-router';
import { replace } from 'react-router-redux';
import { withTranslation } from 'react-i18next';
import config from '../../../config';
import { fetchM } from '../../actions/fetchM';
import { flashSuccessMessage, flashErrorMessage } from '../../actions/flash';
import { promptChangesMessage } from '../../actions/prompt';
import { decodeSearch, encodeSearch } from '../../utils/url';
import isNumber from '../../utils/numbers/isNumber';
import fileSize from '../../utils/fileSize';
import PageNav from '../../components/PageNav';
import NavBar from '../../components/NavBar';
import UploadModal from '../../components/UploadModal';
import {
  UPLOAD_DOCUMENT_PROMPT,
} from '../../constants/promptMessages';
import { namespaces, TRANSLATION_FILES } from '../../constants/translation';

import Form from './Form';

class List extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }
  static propTypes = {
    location: PropTypes.shape({
      query: PropTypes.object,
      search: PropTypes.string,
    }),
    token: PropTypes.string,
    t: PropTypes.func,
  }
  static getPropsToState = (props) => {
    const loading = props.loading;
    const count = props.count;
    const limit = props.limit;
    const offset = props.offset;
    const data = props.data || [];
    const query = decodeSearch(props.location.search);
    const currentPage = parseInt(query.page, 10) || 1;
    return {
      data,
      loading,
      count,
      limit,
      offset,
      currentPage,
    };
  }
  constructor(props) {
    super(props);
    const state = List.getPropsToState(props);
    this.state = {
      ...state,
      selectedFile: null,
      showDelete: false,
    };
  }
  componentWillReceiveProps(nextProps) {
    const state = List.getPropsToState(nextProps);
    this.setState({ ...state });
  }
  getToken(id, callback) {
    const { store } = this.context;
    store.dispatch(fetchM(`/documents/${id}/`, {
      credentials: 'include',
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }))
      .catch((err) => {
        store.dispatch(flashErrorMessage(err));
      })
      .then((res) => {
        const token = res.headers.get('X-Csrf-Token');
        if (callback) {
          callback(token);
        }
      });
  }
  replaceQuery(query) {
    const { store } = this.context;
    store.dispatch(replace(`/media/documents/?${encodeSearch(query)}`));
  }
  handleChangePage = (page) => {
    const query = decodeSearch(this.props.location.search);
    if (!query.page || !isNumber(query.page) || query.page <= 0) {
      query.page = 1;
    }
    const currentPage = parseInt(page || query.page, 10);
    query.page = currentPage;
    this.setState({ currentPage });
    this.replaceQuery(query);
  }
  handleCancelDelete = (e) => {
    e.preventDefault();
    this.setState({
      showDelete: false,
      selectedFile: null,
    });
  }
  handleDelete = (e, selectedFile) => {
    e.preventDefault();
    this.setState({
      selectedFile,
      showDelete: true,
    });
  }
  handleConfirmDelete = (e) => {
    e.preventDefault();
    this.setState({
      showDelete: false,
      loading: true,
    }, () => {
      this.getToken(this.state.selectedFile.id, (token) => { this.deleteDocument(token); });
    });
  }
  async deleteDocument(token) {
    const { t } = this.props;
    const { store } = this.context;
    let res;
    try {
      res = await store.dispatch(fetchM(`/documents/${this.state.selectedFile.id}/`, {
        credentials: 'include',
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': token,
        },
      }));
    } catch (err) {
      await this.setState({ selectedFile: null, loading: false });
      if (err.res && err.res.status >= 400 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
        return;
      }
      store.dispatch(flashErrorMessage(err));
      return;
    }
    this.setState({
      selectedFile: null,
      loading: false,
    });
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      this.handleChangePage();
      store.dispatch(flashSuccessMessage({ header: t('Delete Success', { ns: TRANSLATION_FILES.translation }), message: t('Document successfully deleted', { ns: TRANSLATION_FILES.translation }) }));
    }
  }
  async uploadDocument(e, body) {
    const { t } = this.props;
    e.preventDefault();
    const { store } = this.context;
    try {
      await this.form.submit('/documents/', {
        credentials: 'include',
        method: 'POST',
        headers: {
          // Let browser decide Content-Type
          'X-Csrf-Token': this.props.token,
        },
        body,
      });
    } catch (err) {
      if (err.res && err.res.status >= 400 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
        return;
      }
      store.dispatch(flashErrorMessage(err));
      return;
    }
    this.uploadModal.close();
    this.handleChangePage();
    store.dispatch(flashSuccessMessage({ header: t('Upload Success', { ns: TRANSLATION_FILES.translation }), message: t('Documents(s) successfully uploaded', { ns: TRANSLATION_FILES.translation }) }));
  }
  handleCheckBeforeClose = () => {
    const { t } = this.props;
    if (this.form.state.uploads.length > 0) {
      const callback = () => {
        this.uploadModal.close();
        this.handleChangePage();
      };
      const { store } = this.context;
      store.dispatch(promptChangesMessage({ message: t(UPLOAD_DOCUMENT_PROMPT, { ns: TRANSLATION_FILES.translation }), callback }));
      return true;
    }
    return false;
  }
  render() {
    const { t } = this.props;
    return (
      <Container>
        <NavBar
          header={t('Documents')}
          rightNav={
            <Menu.Item fitted>
              <UploadModal
                ref={(ref) => { this.uploadModal = ref; }}
                loading={this.state.loading}
                onConfirm={(e, data) => {
                  this.uploadDocument(e, data);
                }}
                onClose={this.handleCheckBeforeClose}
                content={({ onConfirm }) => (
                  <Form
                    ref={(ref) => { this.form = ref; }}
                    onSubmit={onConfirm}
                  />
                )}
              />
            </Menu.Item>
          }
        />
        <Modal
          open={this.state.showDelete}
          onClose={this.handleCancelDelete}
        >
          <Header icon="warning sign" content={`${t('Confirm delete')}?`} />
          <Modal.Content>
            <Modal.Description>
              <Header
                as="h3"
                content={`${t('Are you sure to delete this file')}?`}
                subheader={this.state.selectedFile ? this.state.selectedFile.name : ''}
              />
            </Modal.Description>
          </Modal.Content>
          <Modal.Actions>
            <Button basic negative onClick={this.handleCancelDelete} icon="cancel" content={t('No')} inverted />
            <Button basic positive onClick={this.handleConfirmDelete} icon="trash" content={t('Yes')} inverted />
          </Modal.Actions>
        </Modal>
        <Dimmer
          inverted style={{ zIndex: 20 }}
          active={this.state.loading && this.state.data.length > 0}
        >
          <Loader inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
        </Dimmer>
        <Table
          striped unstackable
          selectable
        >
          <Table.Header fullWidth>
            <Table.Row>
              <Table.HeaderCell colSpan={4} textAlign="right">
                <PageNav
                  currentPage={this.state.currentPage}
                  count={this.state.count}
                  limit={this.state.limit}
                  offset={this.state.offset}
                  dataSize={this.state.data.length}
                  loading={this.state.loading}
                  onChange={this.handleChangePage}
                />
              </Table.HeaderCell>
            </Table.Row>
            <Table.Row>
              <Table.HeaderCell>{t('Name')}</Table.HeaderCell>
              <Table.HeaderCell>{t('Size')}</Table.HeaderCell>
              <Table.HeaderCell>{t('Tags')}</Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {
              this.state.data.length > 0 ?
                this.state.data.map((file, key) => (
                  <Table.Row key={key}>
                    <Table.Cell width={6}>
                      <a
                        href={`${config.get('bookApiURL')}/documents/${file.id}/view/${encodeURIComponent(file.name)}.pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Icon name="file pdf outline" />
                        {file.name}
                      </a>
                    </Table.Cell>
                    <Table.Cell collapsing>
                      {fileSize(file.size)}
                    </Table.Cell>
                    <Table.Cell>
                      <Label.Group size="tiny">
                        {
                          file.tags && file.tags.length > 0 ?
                          file.tags.map((tag, idx) => (
                            <Label key={idx} as="a">{tag}</Label>
                          ))
                          : <p>{t('No tags set')}</p>
                        }
                      </Label.Group>
                    </Table.Cell>
                    <Table.Cell collapsing textAlign="right">
                      <Button
                        as={Link}
                        size="tiny"
                        compact
                        primary
                        icon="edit"
                        disabled={this.state.loading}
                        to={`/media/documents/${file.id}/`}
                      />
                      <Button
                        size="tiny"
                        compact
                        negative
                        icon="delete"
                        disabled={this.state.loading}
                        onClick={e => this.handleDelete(e, file)}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))
              :
                <Table.Row>
                  <Table.Cell colSpan={4} textAlign="center">
                    {
                      this.state.loading ?
                        <Loader active inline="centered">Loading</Loader>
                      : t('No documents found')
                    }
                  </Table.Cell>
                </Table.Row>
            }
          </Table.Body>
          <Table.Footer fullWidth>
            <Table.Row>
              <Table.HeaderCell colSpan={4} textAlign="right">
                <PageNav
                  upward
                  currentPage={this.state.currentPage}
                  count={this.state.count}
                  limit={this.state.limit}
                  offset={this.state.offset}
                  dataSize={this.state.data.length}
                  loading={this.state.loading}
                  onChange={this.handleChangePage}
                />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        </Table>
      </Container>
    );
  }
}

export default withTranslation(namespaces)(List);



// WEBPACK FOOTER //
// ./src/containers/Documents/List.jsx